import { useState, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import { useRemoveFolder, useAlertDialog } from '@/hooks';
import type {
    UseFolderManagementOptions,
    UseFolderManagementReturn,
    FolderActionParams,
    RenameFolderState,
} from './types';

const ENTITY_LABELS = {
    workflow: 'workflows',
    tool: 'tools',
    toolbox: 'toolboxes',
    prompt: 'prompts',
} as const;

function useFolderManagement({
    agentId,
    entityType,
    currentPath,
    setCurrentPath,
}: UseFolderManagementOptions): UseFolderManagementReturn {
    const { confirm } = useAlertDialog();
    const entityLabel = ENTITY_LABELS[entityType];

    const { removeFolder } = useRemoveFolder({
        agentId,
        entityType,
    });

    const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false);
    const [createFolderParentPath, setCreateFolderParentPath] = useState('');

    const [renameFolderState, setRenameFolderState] = useState<RenameFolderState>({
        isOpen: false,
        folderId: '',
        folderName: '',
        folderPath: '',
    });

    const breadcrumbSegments = useMemo(() => {
        if (!currentPath) return [];
        return currentPath.split('/').filter(Boolean);
    }, [currentPath]);

    const handleCreateFolder = useCallback(() => {
        setCreateFolderParentPath(currentPath);
        setIsCreateFolderModalOpen(true);
    }, [currentPath]);

    const handleCreateSubfolder = useCallback((parentPath: string) => {
        setCreateFolderParentPath(parentPath);
        setIsCreateFolderModalOpen(true);
    }, []);

    const handleCloseCreateFolder = useCallback(() => {
        setIsCreateFolderModalOpen(false);
        setCreateFolderParentPath('');
    }, []);

    const handleRenameFolder = useCallback((params: FolderActionParams) => {
        setRenameFolderState({
            isOpen: true,
            folderId: params.folderId,
            folderName: params.folderName,
            folderPath: params.folderPath,
        });
    }, []);

    const handleCloseRenameFolder = useCallback(() => {
        setRenameFolderState(prev => ({ ...prev, isOpen: false }));
    }, []);

    const handleDeleteFolder = useCallback(
        async (params: FolderActionParams) => {
            const confirmed = await confirm({
                title: 'Delete Folder',
                description: `Are you sure you want to delete the folder "${params.folderName}"? This action cannot be undone.`,
                confirmLabel: 'Delete',
                cancelLabel: 'Cancel',
            });

            if (confirmed) {
                try {
                    await removeFolder(params.folderId);
                    toast.success(`Folder "${params.folderName}" deleted successfully`);

                    if (
                        currentPath === params.folderPath ||
                        currentPath.startsWith(params.folderPath + '/')
                    ) {
                        setCurrentPath('');
                    }
                } catch (error) {
                    const apiError = error as { code?: string; message?: string };
                    const errorMessage =
                        error instanceof Error
                            ? error.message
                            : apiError?.message || 'Failed to delete folder';
                    const errorCode = apiError?.code;

                    if (errorCode === 'FOLDER_NOT_EMPTY' || errorMessage.includes('contains')) {
                        toast.error(
                            `Cannot delete folder "${params.folderName}" because it contains ${entityLabel}. Move or delete the ${entityLabel} first.`
                        );
                    } else {
                        toast.error(errorMessage);
                    }
                }
            }
        },
        [confirm, removeFolder, currentPath, setCurrentPath, entityLabel]
    );

    const handleBreadcrumbClick = useCallback(
        (index: number) => {
            if (index === -1) {
                setCurrentPath('');
            } else {
                const path = '/' + breadcrumbSegments.slice(0, index + 1).join('/');
                setCurrentPath(path);
            }
        },
        [breadcrumbSegments, setCurrentPath]
    );

    return {
        isCreateFolderModalOpen,
        createFolderParentPath,
        renameFolderState,
        handleCreateFolder,
        handleCreateSubfolder,
        handleCloseCreateFolder,
        handleRenameFolder,
        handleCloseRenameFolder,
        handleDeleteFolder,
        breadcrumbSegments,
        handleBreadcrumbClick,
    };
}

export default useFolderManagement;
export type { FolderActionParams, RenameFolderState, FolderEntityType } from './types';
